<template>
  <div :data-view-id="componentUniqueTitle || uniqueTitle">
  <b-container fluid class="cards-container">
    <ViewTitle :title="title" />
    <b-row v-if="!detail && visiblePresetFilterButtons.length" class="mb-2">
      <b-col>
        <PresetFilterButtons
          :buttons="visiblePresetFilterButtons"
          :active-ids="activePresetFilterIds"
          @toggle="onPresetFilterToggle"
        />
      </b-col>
    </b-row>
    <b-row v-if="searchEnabled" class="mb-2">
      <b-col>
        <ViewSearch :model-value="searchText" @update:model-value="onSearchInput" />
      </b-col>
    </b-row>
    <b-row v-if="userFiltering" class="mb-3">
      <b-col>
        <RuntimeFilterBuilder
          ref="filterBuilder"
          :field-groups="filterFieldGroups"
          :initial-filters="resolvedInitialFilters"
          :initial-match-mode="resolvedInitialMatchMode"
          @filter-changed="onFilterChanged"
        />
      </b-col>
    </b-row>
    <div
      class="cards-grid"
      :class="{
        'cards-grid--one-per-row': cardColumnCount === 1,
        'cards-grid--counted': !!cardColumnCount
      }"
      :style="cardGridStyle"
    >
      <b-card
        v-for="(item, index) in displayItems"
        :key="index"
        class="card-item"
      >
        <div class="card-body-inner">
          <div
            v-for="(row, rowIndex) in cardRows"
            :key="'row-' + rowIndex"
            class="card-layout-row"
          >
            <div
              v-for="(column, columnIndex) in row.columns"
              :key="'column-' + columnIndex"
              class="card-layout-column"
              :style="cardColumnStyle(column)"
            >
              <div
                v-for="(field, fieldIndex) in column.fields"
                :key="field.key + '-' + fieldIndex"
                class="card-field"
                :class="{ 'card-field--link': field.type === 'link' }"
                :data-field="field.columnUniqueTitle || null"
              >
                <small v-if="field.label" class="card-field-label text-muted fw-semibold text-uppercase">{{ field.label }}</small>
                <span v-if="field.type !== 'link'" class="card-field-value" :style="cellFormatStyle(item, field)" v-html="cellHtml(item, field)"></span>
                <button
                  v-if="field.type === 'link' && field.linkMode === 'code'"
                  type="button"
                  class="card-link"
                  :class="field.displayAsButton ? 'view-link-button' : 'card-code-link'"
                  @click="runLinkCode(item, field, $event)"
                >
                  {{ field.customText || item[field.key] || field.label || 'Run' }}
                </button>
                <router-link
                  v-else-if="field.type === 'link' && getCardLinkUrl(item, field)"
                  :to="getCardLinkUrl(item, field)"
                  class="card-link"
                  :class="{ 'view-link-button': field.displayAsButton }"
                >
                  {{ field.customText || item[field.key] || field.label || 'Open' }}
                </router-link>
                <span
                  v-if="field.type === 'link' && field.linkMode !== 'code' && !getCardLinkUrl(item, field)"
                  :class="field.displayAsButton ? 'view-link-button is-disabled' : 'card-link-disabled'"
                >
                  {{ field.customText || item[field.key] || field.label || 'Open' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </b-card>
    </div>
    <div v-if="!displayItems || displayItems.length === 0" class="text-center text-muted py-5" role="status" aria-live="polite">
      {{ detail ? 'Open a record to see its details.' : 'No items to display.' }}
    </div>
  </b-container>
  </div>
</template>

<script>
import _ from 'lodash'
import axios from 'axios'
import RuntimeFilterBuilder from './RuntimeFilterBuilder.vue'
import ViewTitle from './ViewTitle.vue'
import ViewSearch from './ViewSearch.vue'
import { displayListValue } from '../lib/multiselect-display'
import { fieldDisplayValue, shownField } from '../../../shared/field-display'
import { realtimeSubscribeFields } from '../lib/view-visibility'
import { liveAppWebSocketUrl } from '../lib/live-app-socket'
import { eventRefreshesView, fieldIndexMap } from '../../../shared/view-refresh'
import { viewSearchParam } from '../../../shared/view-search'
import { filterQueryHref, readFilterQuery } from '../lib/filter-query'
import PresetFilterButtons from './PresetFilterButtons.vue'
import { presetFilterMixin } from '../lib/preset-filter-state'
import { formattingToStyle } from '../../../shared/display-rules'
import { formatAppInstant } from '../lib/app-time-zone'
import { imageCellHtml, isViewImageMarkup } from '../../../shared/image-column'
import { escapeHtml, sanitizeHtml } from '../../../shared/html-sanitize'
import { cardGridColumns, normalizeCardsPerRow } from '../../../shared/card-layout'
import { columnFormatStyle } from '../../../shared/column-format'
import { runViewLinkCode } from '../lib/run-custom-code'

export default {
  mixins: [presetFilterMixin],
  components: {
    RuntimeFilterBuilder,
    ViewTitle,
    ViewSearch,
    PresetFilterButtons
  },
  data () {
    return {
      filterFieldGroups: [],
      filteredItems: null,
      urlFilters: null,
      urlMatchMode: null,
      activeFilterEvent: null,
      searchText: '',
      ws: null
    }
  },
  props: {
    title: { type: String, default: '' },
    uniqueTitle: { type: String, default: '' },
    userFiltering: { type: Boolean },
    realtime: { type: Boolean, default: false },
    spreadSheetUniqueTitle: { type: String, default: '' },
    fields: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    field: { type: Object, default: () => ({}) },
    pageUniqueTitle: { type: String, default: '' },
    componentUniqueTitle: { type: String, default: '' },
    fieldCounterMap: { type: Object, default: null },
    defaultUserFilters: { type: Array, default: () => [] },
    defaultUserFilterMatchMode: { type: String, default: 'all' },
    restrictUserFiltering: { type: Boolean, default: false },
    searchEnabled: { type: Boolean, default: false },
    oneCardPerRow: { type: Boolean, default: false },
    cardsPerRow: { type: Number, default: 0 },
    detail: { type: Boolean, default: false },
    layout: { type: Array, default: () => [] }
  },
  computed: {
    cardColumnCount () {
      if (this.detail) return 1
      const count = normalizeCardsPerRow(this.cardsPerRow)
      if (count) return count
      if (this.oneCardPerRow) return 1
      return null
    },
    cardGridStyle () {
      const columns = cardGridColumns(this.cardColumnCount)
      if (!columns) return null
      return { '--card-columns': columns }
    },
    cardRows () {
      if (Array.isArray(this.layout) && this.layout.length) return this.layout
      return [{ columns: [{ fields: this.fields || [] }] }]
    },
    displayItems () {
      return this.filteredItems !== null ? this.filteredItems : this.items
    },
    filterQueryKey () {
      return `f_${this.componentUniqueTitle || this.uniqueTitle}`
    },
    searchQueryKey () {
      return viewSearchParam(this.componentUniqueTitle || this.uniqueTitle)
    },
    searchTerm () {
      return String(this.searchText || '').trim()
    },
    resolvedInitialFilters () {
      if (this.urlFilters) return this.urlFilters
      return this.defaultUserFilters
    },
    resolvedInitialMatchMode () {
      if (this.urlMatchMode) return this.urlMatchMode
      return this.defaultUserFilterMatchMode
    }
  },
  mounted () {
    if (this.userFiltering) {
      this.readFiltersFromUrl()
      this.loadFilterFields()
    }
    if (this.searchEnabled) {
      this.readSearchFromUrl()
      if (this.searchTerm) this.fetchCardsData(this.activeFilterEvent, { alwaysApply: true })
    }
    if (this.realtime) {
      this.connectRealtimeWebSocket()
    }
    this._onRefreshViews = (event) => {
      if (!eventRefreshesView(event, this.componentUniqueTitle || this.uniqueTitle)) return
      this.refreshData()
    }
    window.addEventListener('superturbo-refresh-views', this._onRefreshViews)
  },
  beforeUnmount () {
    clearTimeout(this._searchTimer)
    this.disconnectRealtimeWebSocket()
    if (this._onRefreshViews) {
      window.removeEventListener('superturbo-refresh-views', this._onRefreshViews)
    }
  },
  watch: {
    userFiltering (val) {
      if (val && !this.filterFieldGroups.length) {
        this.loadFilterFields()
      }
    }
  },
  methods: {
    cardColumnStyle (column) {
      return columnFormatStyle(column && column.columnFormat)
    },
    runLinkCode (item, field, event) {

      const card = event && event.currentTarget && event.currentTarget.closest('.card-item')

      return runViewLinkCode(field && field.code, item, card, 'card')
    },
    getCardLinkUrl (item, field) {
      if (!item.links) return null
      const linkKey = field.linkKey || field.childPageUniqueTitle
      const rawLink = item.links[linkKey]
      if (!rawLink) return null
      if (rawLink.includes('/')) {
        return `${(this.routePath || this.$route.path).replace(/\/+$/, '')}/${rawLink}`
      }
      return `/${this.$route.params.application}/${rawLink}`
    },
    async loadFilterFields () {
      const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
      const compUT = this.componentUniqueTitle || this.title
      if (!pageUT || !compUT) return

      const account = this.routeAccount
      const application = this.$route?.params?.application || ''

      try {
        let url = `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}/filterfields`
        if (this.restrictUserFiltering) {
          url += '?restrict=true'
        }
        const response = await axios.get(url)
        this.filterFieldGroups = response.data
      } catch (err) {
        console.warn('Failed to load filter fields:', err)
      }
    },
    compactFilter (filter) {
      const compact = {}
      for (const [k, v] of Object.entries(filter)) {
        if (v === null || v === undefined) continue
        if (k === 'valueFieldRef' && v !== null) {
          compact[k] = this.compactFilter(v)
        } else {
          compact[k] = v
        }
      }
      return compact
    },
    expandFilter (compact) {
      const defaults = {
        relationshipColumn: null,
        relatedSpreadsheet: null,
        sourceSpreadsheet: null,
        sourceRelationshipColumn: null,
        relQuantifier: null,
        valueType: 'literal',
        value: '',
        valueFieldRef: null
      }
      const full = { ...defaults, ...compact }
      if (full.valueFieldRef && typeof full.valueFieldRef === 'object') {
        full.valueFieldRef = this.expandFilter(full.valueFieldRef)
      }
      return full
    },
    filtersMatchDefaults (filterEvent) {
      const defaults = this.defaultUserFilters || []
      const current = (filterEvent && filterEvent.filters) || []
      const currentMode = (filterEvent && filterEvent.matchMode) || 'all'
      const defaultMode = this.defaultUserFilterMatchMode || 'all'
      if (currentMode !== defaultMode) return false
      if (current.length !== defaults.length) return false
      return JSON.stringify(current) === JSON.stringify(defaults)
    },
    readFiltersFromUrl () {
      const parsed = readFilterQuery(window.location.search, this.filterQueryKey)
      if (!parsed) return
      this.urlFilters = parsed.filters.map(f => this.expandFilter(f))
      this.urlMatchMode = parsed.matchMode
    },
    writeFiltersToUrl (filterEvent) {
      try {
        const hasFilters = filterEvent && filterEvent.filters && filterEvent.filters.length > 0
        let payload = null
        if (hasFilters && !this.filtersMatchDefaults(filterEvent)) {
          payload = { filters: filterEvent.filters.map(f => this.compactFilter(f)) }
          if ((filterEvent.matchMode || 'all') !== 'all') {
            payload.matchMode = filterEvent.matchMode
          }
        }
        window.history.replaceState(null, '', filterQueryHref(window.location.href, this.filterQueryKey, payload))
      } catch (err) {
        console.warn('Failed to write filters to URL:', err)
      }
    },
    readSearchFromUrl () {
      try {
        const params = new URLSearchParams(window.location.search)
        this.searchText = params.get(this.searchQueryKey) || ''
      } catch (err) {
        this.searchText = ''
      }
    },
    writeSearchToUrl () {
      try {
        const url = new URL(window.location.href)
        if (this.searchTerm) url.searchParams.set(this.searchQueryKey, this.searchTerm)
        else url.searchParams.delete(this.searchQueryKey)
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
      } catch (err) {
        console.warn('Failed to write search to URL:', err)
      }
    },
    onSearchInput (value) {
      this.searchText = value
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => {
        this.writeSearchToUrl()
        this.fetchCardsData(this.activeFilterEvent)
      }, 300)
    },
    async onFilterChanged (filterEvent) {
      this.writeFiltersToUrl(filterEvent)
      this.activeFilterEvent = filterEvent

      await this.fetchCardsData(filterEvent)
    },
    reloadForPresetFilters () {
      return this.fetchCardsData(this.activeFilterEvent, { alwaysApply: true })
    },
    async refreshData () {
      if (window.__superturbo_preview_mode) return
      if (this.detail) {
        await this.fetchDetailRecord()
        return
      }
      await this.fetchCardsData(this.activeFilterEvent, { alwaysApply: true })
    },
    async fetchDetailRecord () {
      const accessKey = this.routeAccessKey
      if (!accessKey) {
        this.filteredItems = []
        return
      }

      const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
      const compUT = this.componentUniqueTitle || this.uniqueTitle
      const account = this.routeAccount
      const application = this.$route?.params?.application || ''
      const url = `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}/accesskey/${encodeURIComponent(accessKey)}`

      try {
        const response = await axios.get(url)
        const row = response.data
        this.filteredItems = this.transformResponseData(row && typeof row === 'object' && !Array.isArray(row) ? [row] : [])
      } catch (err) {
        console.warn('Failed to refresh the record for this card:', err)
        this.filteredItems = []
      }
    },
    async fetchCardsData (filterEvent, { alwaysApply = false } = {}) {
      const hasFilters = filterEvent && filterEvent.filters && filterEvent.filters.length > 0
      const hasSearch = this.searchEnabled && !!this.searchTerm
      const hasPreset = this.activePresetFilterIds.length > 0
      if (!alwaysApply && !hasFilters && !hasSearch && !hasPreset) {
        this.filteredItems = null
        return
      }

      const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
      const compUT = this.componentUniqueTitle || this.uniqueTitle
      const account = this.routeAccount
      const application = this.$route?.params?.application || ''

      let url = `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}?limit=25`
      if (hasFilters) {
        url += `&userFilters=${encodeURIComponent(JSON.stringify(filterEvent.filters))}`
        url += `&userFilterMatchMode=${encodeURIComponent(filterEvent.matchMode || 'all')}`
      }
      if (hasSearch) {
        url += `&search=${encodeURIComponent(this.searchTerm)}`
      }
      if (this.presetFilterQuery()) {
        url += `&presetFilters=${encodeURIComponent(this.presetFilterQuery())}`
      }

      const accessKey = this.routeAccessKey
      if (accessKey) {
        url += `&accessKey=${encodeURIComponent(accessKey)}`
      }

      const requestId = (this._cardsRequestId = (this._cardsRequestId || 0) + 1)

      try {
        const response = await axios.get(url)
        if (requestId !== this._cardsRequestId) return
        this.filteredItems = this.transformResponseData(response.data)
      } catch (err) {
        if (requestId !== this._cardsRequestId) return
        console.warn('Failed to fetch cards data:', err)
      }
    },
    connectRealtimeWebSocket () {
      try {
        this.ws = new WebSocket(liveAppWebSocketUrl(VUE_APP_APP_SERVER_WS_URL, this.routeAccount, this.$route.params.application))
      } catch (err) {
        console.warn('Realtime WebSocket connection failed:', err)
        return
      }

      this.ws.onopen = () => {
        this.ws.send(JSON.stringify({
          type: 'realtime_subscribe',
          pageUniqueTitle: this.pageUniqueTitle,
          componentUniqueTitle: this.componentUniqueTitle,
          spreadSheetUniqueTitle: this.spreadSheetUniqueTitle,
          ...realtimeSubscribeFields({
            ...(this.$route && this.$route.params),
            account: this.routeAccount
          })
        }))
      }

      this.ws.onmessage = (event) => {
        let msg
        try {
          msg = JSON.parse(event.data)
        } catch { return }

        if (msg.type === 'realtime_update' &&
            msg.pageUniqueTitle === this.pageUniqueTitle &&
            msg.componentUniqueTitle === this.componentUniqueTitle) {
          this.refreshData()
        }
      }

      this.ws.onclose = () => {
        setTimeout(() => {
          if (this.realtime && !this._isBeingDestroyed) {
            this.connectRealtimeWebSocket()
          }
        }, 3000)
      }

      this.ws.onerror = () => {}
    },
    disconnectRealtimeWebSocket () {
      if (this.ws) {
        if (this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({
            type: 'realtime_unsubscribe',
            pageUniqueTitle: this.pageUniqueTitle,
            componentUniqueTitle: this.componentUniqueTitle
          }))
        }
        this.ws.onclose = null
        this.ws.close()
        this.ws = null
      }
    },
    cellFormatStyle (item, field) {
      if (!item._formatting || !item._formatting[field.key]) return {}
      return formattingToStyle(item._formatting[field.key])
    },
    cellFormatHidden (item, field) {
      return item._formatting && item._formatting[field.key] && item._formatting[field.key].hidden
    },
    cellHtml (item, field) {
      if (this.cellFormatHidden(item, field)) return ''
      if (field.customText) return sanitizeHtml(field.customText)
      const stored = item._raw && item._raw[field.key] != null ? item._raw[field.key] : item[field.key]
      const shown = shownField(fieldDisplayValue(stored, item[field.key]), {
        multiselectDisplay: field.multiselectDisplay,
        valueFormatting: field.valueFormatting
      })
      if (shown) return shown.html
      const value = item[field.key]
      if (isViewImageMarkup(value)) return value
      if (typeof value === 'string' && value.indexOf('<a class="view-file"') === 0) return value
      if (typeof value === 'string') return value
      return escapeHtml(value)
    },
    transformResponseData (data) {
      const fcm = fieldIndexMap(this.fieldCounterMap, this.fields)
      if (!fcm) return data

      return _.map(data, (row) => {
        const newRow = { _values: row }
        // Preserve _formatting
        if (row._formatting) {
          newRow._formatting = row._formatting
        }
        // Preserve link data from server
        if (row._links) newRow._links = row._links
        if (row.links) newRow.links = row.links

        for (const key of Object.keys(row)) {
          if (key === '_formatting' || key === '_links' || key === 'links') continue
          if (key.endsWith('_filename') || key.endsWith('_type')) continue
          const column = row[key]
          if (typeof fcm[key] !== 'number') continue
          if (!newRow._raw) newRow._raw = {}
          newRow._raw[fcm[key]] = column

          const imageHtml = imageCellHtml(row, key)
          if (imageHtml != null) {
            newRow[fcm[key]] = imageHtml
          } else {
            const field = (this.fields || []).find((item) => item.columnUniqueTitle === key)
            const listed = displayListValue(column, field && field.multiselectDisplay)
            const text = listed != null ? listed : column
            newRow[fcm[key]] = typeof text === 'string' ? escapeHtml(formatAppInstant(text)) : text
          }
        }
        return newRow
      })
    }
  }
}
</script>

<style scoped>
.cards-container {
  padding: 1rem 0;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  text-align: left;
}

.cards-grid--counted {
  grid-template-columns: var(--card-columns);
}

.cards-grid--one-per-row {
  grid-template-columns: minmax(0, 1fr);
}

.card-item {
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.card-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

.card-body-inner {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.card-layout-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-start;
}

.card-layout-column {
  flex: 1 1 8rem;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.card-field {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.card-field-label {
  font-size: 0.675rem;
  letter-spacing: 0.06em;
}

.card-field-value {
  font-size: 0.95rem;
  line-height: 1.4;
  word-break: break-word;
}

.card-field-value:deep(img) {
  max-width: 100%;
  margin-top: 0.25rem;
}

.card-field-value:deep(img.rich-text-image) {
  max-width: 200px;
  max-height: 150px;
  object-fit: cover;
  cursor: pointer;
  margin: 2px 4px;
}

.card-field-value:deep(pre) {
  padding: 8px;
  margin: 4px 0;
  font-size: 13px;
  overflow-x: auto;
}

.card-field-value:deep(code) {
  padding: 1px 4px;
  font-size: 13px;
}

.card-field--link {
  margin-top: auto;
  padding-top: 0.625rem;
  border-top: 1px solid var(--bs-border-color);
}

.card-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.15s ease;
}

button.card-code-link {
  background: none;
  border: 0;
  padding: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
}

button.card-code-link::after {
  content: none;
}

.card-link::after {
  content: '\2192';
  font-size: 1rem;
  transition: transform 0.15s ease;
}

.card-link:hover::after {
  transform: translateX(3px);
}

.card-link.view-link-button,
.card-link.view-link-button:hover {
  color: #fff;
  text-decoration: none;
}

.card-link.view-link-button::after {
  content: none;
}

.card-link-disabled {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  font-weight: 500;
  color: #999;
  font-style: italic;
}
</style>
